import * as Msal from "@azure/msal-browser";

// MSAL configuration
const msalConfig = {
  auth: {
    clientId: "25267ae8-be0d-4909-b637-6fb336e2d1f3",
    authority:
      "https://login.microsoftonline.com/1c926ae6-87e5-4c0e-b7d5-aca4983832b7",
    redirectUri: `${location.origin}/login`,
  },
  cache: {
    cacheLocation: "sessionStorage", // This configures where your cache will be stored
    storeAuthStateInCookie: false, // Set this to "true" if you're having issues on IE11 or Edge
  },
};

// Create MSAL instance
let msalInstance = null;

export function getMsalInstance() {
  if (!msalInstance) {
    msalInstance = new Msal.PublicClientApplication(msalConfig);
  }
  return msalInstance;
}

// Check if user has an active MSAL session
export function hasActiveMsalSession() {
  const instance = getMsalInstance();
  const accounts = instance.getAllAccounts();
  return accounts.length > 0;
}

// Logout from MSAL/Azure AD (app-specific only, preserves O365 session)
export async function logoutFromMsal() {
  try {
    const instance = getMsalInstance();
    const accounts = instance.getAllAccounts();

    console.log("MSAL Service: Starting app-specific MSAL session clearing");
    console.log("MSAL Service: Found accounts:", accounts.length);

    if (accounts.length > 0) {
      console.log(
        "MSAL Service: Clearing app-specific MSAL session (preserving O365 session)",
      );

      // Clear any MSAL-specific session storage for this app
      const msalKeys = [];
      for (let i = 0; i < sessionStorage.length; i++) {
        const key = sessionStorage.key(i);
        if (
          key &&
          (key.includes(msalConfig.auth.clientId) ||
            key.includes("msal") ||
            key.includes("azure") ||
            key.includes("adal"))
        ) {
          msalKeys.push(key);
        }
      }

      console.log("MSAL Service: Clearing session storage keys:", msalKeys);
      msalKeys.forEach((key) => {
        sessionStorage.removeItem(key);
      });

      // Also clear any localStorage keys related to MSAL for this app
      const localMsalKeys = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (
          key &&
          (key.includes(msalConfig.auth.clientId) ||
            key.includes("msal") ||
            key.includes("azure") ||
            key.includes("adal"))
        ) {
          localMsalKeys.push(key);
        }
      }

      console.log("MSAL Service: Clearing localStorage keys:", localMsalKeys);
      localMsalKeys.forEach((key) => {
        localStorage.removeItem(key);
      });

      console.log(
        "MSAL Service: App-specific MSAL session cleared successfully",
      );
      return true;
    } else {
      console.log("MSAL Service: No active MSAL accounts found");
      return false;
    }
  } catch (error) {
    console.error(
      "MSAL Service: Error during app-specific MSAL logout:",
      error,
    );
    throw error;
  }
}

// Alternative: Clear MSAL session without redirect (for popup mode)
export async function clearMsalSession() {
  try {
    const instance = getMsalInstance();
    const accounts = instance.getAllAccounts();

    if (accounts.length > 0) {
      console.log("MSAL Service: Clearing MSAL session and cache");

      // Clear session storage manually
      sessionStorage.clear();

      return true;
    }

    return false;
  } catch (error) {
    console.error("MSAL Service: Error clearing MSAL session:", error);
    throw error;
  }
}

// Complete session cleanup - clears everything
export async function forceLogoutCleanup() {
  try {
    console.log("MSAL Service: Performing complete session cleanup");

    // Clear all browser storage
    localStorage.clear();
    sessionStorage.clear();

    console.log("MSAL Service: Complete session cleanup finished");
    return true;
  } catch (error) {
    console.error("MSAL Service: Error during force cleanup:", error);
    return false;
  }
}

// Login with MSAL
export async function loginWithMsal() {
  try {
    const instance = getMsalInstance();
    const loginRequest = {
      scopes: ["openid", "profile", "User.Read"],
    };

    await instance.loginRedirect(loginRequest);
  } catch (error) {
    console.error("MSAL Service: Error during MSAL login:", error);
    throw error;
  }
}

// Get MSAL token silently
export async function getMsalToken(account) {
  try {
    const instance = getMsalInstance();
    const tokenRequest = {
      scopes: ["api://25267ae8-be0d-4909-b637-6fb336e2d1f3/ssr-api"],
      account: account,
    };

    const tokenResponse = await instance.acquireTokenSilent(tokenRequest);
    return tokenResponse.accessToken;
  } catch (error) {
    if (error instanceof Msal.InteractionRequiredAuthError) {
      // If silent token acquisition fails, redirect to get new token
      const instance = getMsalInstance();
      const tokenRequest = {
        scopes: ["api://25267ae8-be0d-4909-b637-6fb336e2d1f3/ssr-api"],
        account: account,
      };
      await instance.acquireTokenRedirect(tokenRequest);
    } else {
      throw error;
    }
  }
}

// Reset MSAL instance and clear any interaction state
export async function resetMsalState() {
  try {
    console.log(
      "MSAL Service: Resetting MSAL state and clearing interaction locks",
    );

    // Clear all browser storage that might contain MSAL state
    const keysToRemove = [];

    // Check sessionStorage
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (
        key &&
        (key.includes("msal") ||
          key.includes("azure") ||
          key.includes("adal") ||
          key.includes(msalConfig.auth.clientId))
      ) {
        keysToRemove.push(key);
      }
    }

    // Check localStorage
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (
        key &&
        (key.includes("msal") ||
          key.includes("azure") ||
          key.includes("adal") ||
          key.includes(msalConfig.auth.clientId))
      ) {
        keysToRemove.push(key);
      }
    }

    // Remove all MSAL-related keys
    keysToRemove.forEach((key) => {
      sessionStorage.removeItem(key);
      localStorage.removeItem(key);
    });

    console.log(
      "MSAL Service: MSAL state reset completed, removed keys:",
      keysToRemove,
    );

    // Recreate the MSAL instance to ensure clean state
    msalInstance = null;
    const newInstance = getMsalInstance();

    console.log("MSAL Service: Created fresh MSAL instance");
    return newInstance;
  } catch (error) {
    console.error("MSAL Service: Error resetting MSAL state:", error);
    throw error;
  }
}

export { msalConfig };
